import React, { useState, useRef } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import Textfield from '@atlaskit/textfield';
import AddIcon from '@atlaskit/icon/core/add';
import Form, { Field, ErrorMessage } from '@atlaskit/form';
import type { FormApi } from '@atlaskit/form';
import Spinner from '@atlaskit/spinner';
import { IconButton } from '@atlaskit/button/new';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import CrossIcon from '@atlaskit/icon/core/cross';

import type { ActiveContent } from '@atlassian/ai-audio';

import { useFetchDataFromORS } from '@confluence/embed-utils';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { SourceErrors } from '../types/SourceError.ts';
import type { SourceError } from '../types/SourceError.ts';
import type { ResolvedAtlassianLink } from '../types/ResolvedAtlassianLink.ts';
import { getConfluenceContentAri } from '../../../../../utils/getConfluenceContentAri.ts';
import { isSupportedResource } from '../utils/supportedResourceTypes.ts';

export const i18n = defineMessages({
	focusedPlaceholder: {
		id: 'audio.audio-popup.sources-tab.focused-placeholder',
		defaultMessage: 'Paste a Confluence link...',
		description: 'Placeholder in a textbox when the user has the text box focused',
	},
	unfocusedPlaceholder: {
		id: 'audio.audio-popup.sources-tab.unfocused-placeholder',
		defaultMessage: 'Add a link...',
		description: 'Placeholder in a textbox when the textbox is not focused',
	},
	submitButtonLabel: {
		id: 'audio.audio-popup.sources-tab.submit-button-label',
		defaultMessage: 'Add Link',
		description:
			'Submit button in the sources input field. Clicking adds the pasted Confluence link to audio sources.',
	},
	clearButtonLabel: {
		id: 'audio.audio-popup.sources-tab.clear-button-label',
		defaultMessage: 'Clear',
		description:
			'Clear button in the sources input field. Clicking clears the input without adding a link.',
	},
	[SourceErrors.UNSUPPORTED_URL]: {
		id: 'audio.audio-popup.sources-tab.validation-error.unsupported-url',
		defaultMessage: 'This source is currently not supported.',
		description:
			'Error message shown to the user when an invalid string or URL is pasted in to the textbox',
	},
	[SourceErrors.DUPLICATE]: {
		id: 'audio.audio-popup.sources-tab.validation-error.duplicate',
		defaultMessage: 'This link is already present!',
		description:
			'Error message shown to the user when they paste a link that has already been pasted.',
	},
	[SourceErrors.INVALID_URL]: {
		id: 'audio.audio-popup.sources-tab.validation-error.invalid-url',
		defaultMessage: 'Please paste a valid link',
		description: 'Error message shown to the user when they submit text that is not a link.',
	},
	[SourceErrors.CURRENT_CONTENT]: {
		id: 'audio.audio-popup.sources-tab.validation-error.current-content',
		defaultMessage: 'The active content is already included.',
		description: 'Error message shown to the user when they submit text that is not a link.',
	},
	[SourceErrors.NO_ACCESS]: {
		id: 'audio.audio-popup.sources-tab.validation-error.no-access',
		defaultMessage: 'This content is restricted.',
		description:
			'Error message shown to the user when they submit a link that they do not have access to.',
	},
});

const styles = cssMap({
	container: {
		flex: 1,
	},
	inputContainer: {
		paddingBlock: token('space.025'),
		position: 'relative',
	},
	addIcon: {
		height: '18px',
		marginInlineStart: token('space.025'),
		borderWidth: token('border.width'),
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
		borderRadius: token('radius.small', '3px'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
	},
	textFieldContainer: {
		flexGrow: 1,
		paddingBlock: token('space.negative.400'),
	},
	buttonsContainer: {
		position: 'absolute',
		insetInlineEnd: 0,
		marginBlockStart: token('space.075'),
		zIndex: 200,
	},
	buttonWrapper: {
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.medium'),
		boxShadow: token('elevation.shadow.overlay'),
	},
});

type FormValues = {
	resourceUrl: string;
};

type AdditionalResource = {
	link: string;
	ari: string;
};

const MAX_RESOURCES = 4;

const InputAddIcon = (
	<Box xcss={styles.addIcon}>
		<AddIcon label="" size="small" spacing="compact" />
	</Box>
);

export type SourcesInputProps = {
	additionalResources: {
		link: string;
		ari: string;
	}[];
	addAdditionalResource: (newAdditionalResource: AdditionalResource) => void;
	activeContent: ActiveContent;
};

export const SourcesInput = ({
	additionalResources,
	addAdditionalResource,
	activeContent,
}: SourcesInputProps) => {
	const { cloudId } = useSessionData();
	const { formatMessage } = useIntl();
	const { fetchEmbedData } = useFetchDataFromORS();
	const [inputError, setInputError] = useState<SourceError | undefined>();
	const [isFocused, setIsFocused] = useState(false);

	const isMaxReached = additionalResources.length === MAX_RESOURCES;
	const [placeholder, setPlaceholder] = useState<string>(formatMessage(i18n.unfocusedPlaceholder));
	const inputRef = useRef<HTMLInputElement>(null);
	const validateUrl = (url: string): SourceError | null => {
		try {
			new URL(url);
			return null;
		} catch {
			return SourceErrors.INVALID_URL;
		}
	};

	const validateResourceSupport = (resolvedLink: ResolvedAtlassianLink): SourceError | null => {
		return isSupportedResource(resolvedLink) ? null : SourceErrors.UNSUPPORTED_URL;
	};

	const validateDuplicate = (newResource: AdditionalResource): SourceError | null => {
		const isDuplicate = additionalResources.some(
			(existingResource) => existingResource.ari === newResource.ari,
		);
		return isDuplicate ? SourceErrors.DUPLICATE : null;
	};

	const validateAccess = (resolvedLink: ResolvedAtlassianLink): SourceError | null => {
		return resolvedLink?.access !== 'granted' ? SourceErrors.NO_ACCESS : null;
	};

	const validateNotCurrentContent = (newResource: AdditionalResource): SourceError | null => {
		// activeContent should really be there and have these fields
		// this is just a best effort check anyways
		const { contentType = '', contentId = '' } = activeContent || {};
		const currentAri = getConfluenceContentAri(cloudId, contentId, contentType);
		const submissionMatchesActiveContent = currentAri?.toString() === newResource.ari;
		return submissionMatchesActiveContent ? SourceErrors.CURRENT_CONTENT : null;
	};

	const resetFormWithError = (form: FormApi<FormValues>, error: SourceError): void => {
		form.reset();
		setInputError(error);
		setTimeout(() => {
			inputRef.current?.focus();
		}, 0);
	};

	const resetFormSuccessfully = (form: FormApi<FormValues>): void => {
		form.reset();
		// Submission doesn't trigger the onBlur handler
		setPlaceholder(formatMessage(i18n.unfocusedPlaceholder));
		setIsFocused(false);
	};

	const createNewResource = (
		link: string,
		resolvedLink: ResolvedAtlassianLink,
	): AdditionalResource => ({
		link,
		// we know that ari is not undefined because isSupportedResource would have returned false
		// if we didn't have Confluence as the product or page as the resource type
		ari: resolvedLink.ari!,
	});

	const handleNewResourceSubmit = async (
		values: FormValues,
		form: FormApi<FormValues>,
	): Promise<void> => {
		setInputError(undefined);
		const { resourceUrl } = values;

		const urlError = validateUrl(resourceUrl);
		if (urlError) {
			resetFormWithError(form, urlError);
			return;
		}

		try {
			const resolvedLink = await resolveLink(resourceUrl);

			const supportError = validateResourceSupport(resolvedLink);
			if (supportError) {
				resetFormWithError(form, supportError);
				return;
			}

			const accessError = validateAccess(resolvedLink);
			if (accessError) {
				resetFormWithError(form, accessError);
				return;
			}

			const newResource = createNewResource(resourceUrl, resolvedLink);

			const duplicateError = validateDuplicate(newResource);
			if (duplicateError) {
				resetFormWithError(form, duplicateError);
				return;
			}

			const currentContentError = validateNotCurrentContent(newResource);
			if (currentContentError) {
				resetFormWithError(form, currentContentError);
				return;
			}

			resetFormSuccessfully(form);
			addAdditionalResource(newResource);
		} catch {
			// Handle any unexpected errors during link resolution
			resetFormWithError(form, SourceErrors.INVALID_URL);
		}
	};

	// Only returns resolved Atlassian links
	// Loom will make this a bit complicated down the line
	const resolveLink = async (value: string): Promise<ResolvedAtlassianLink> => {
		const linkData = await fetchEmbedData({ embedUrl: value });

		const resolvedAccess = linkData.meta?.access;
		const resolvedAri = linkData.data?.['atlassian:ari'];
		const resolvedProduct = linkData.meta?.product;
		const resolvedResourceType = linkData.meta?.resourceType;

		return {
			access: resolvedAccess,
			ari: resolvedAri,
			product: resolvedProduct,
			resourceType: resolvedResourceType,
		};
	};

	const handleOnFocus = () => {
		setPlaceholder(formatMessage(i18n.focusedPlaceholder));
		setIsFocused(true);
	};

	const handleOnBlur = (event: React.FocusEvent) => {
		// Only blur if focus is not moving to our action buttons
		const relatedTarget = event.relatedTarget as HTMLElement;
		const isMovingToActionButton = relatedTarget?.closest('[data-testid*="action-button"]');

		if (!isMovingToActionButton) {
			setPlaceholder(formatMessage(i18n.unfocusedPlaceholder));
			setInputError(undefined);
			setIsFocused(false);
		}
	};

	const handleClear = (reset: () => void) => {
		reset();
		setInputError(undefined);
		setPlaceholder(formatMessage(i18n.unfocusedPlaceholder));
		setIsFocused(false);
	};

	return (
		<Form onSubmit={handleNewResourceSubmit}>
			{({ formProps, submitting, reset }) => (
				<form {...formProps}>
					<Field name="resourceUrl" defaultValue="" isDisabled={isMaxReached || submitting}>
						{({ fieldProps }) => (
							<Box xcss={styles.inputContainer}>
								<Textfield
									{...fieldProps}
									ref={inputRef}
									testId="audio-sources-textfield"
									elemAfterInput={submitting && <Spinner size="small" />}
									appearance="subtle"
									elemBeforeInput={InputAddIcon}
									aria-label={formatMessage(i18n.focusedPlaceholder)}
									isInvalid={!!inputError}
									placeholder={placeholder}
									isCompact
									onFocus={handleOnFocus}
									onBlur={handleOnBlur}
								/>
								{inputError && (
									<ErrorMessage>
										<FormattedMessage {...i18n[inputError]} />
									</ErrorMessage>
								)}
								{isFocused && !inputError && !submitting && (
									<Flex xcss={styles.buttonsContainer} gap="space.050">
										<Box xcss={styles.buttonWrapper}>
											<IconButton
												type="submit"
												icon={(iconProps) => <CheckMarkIcon {...iconProps} size="small" />}
												label={formatMessage(i18n.submitButtonLabel)}
												isDisabled={!fieldProps.value}
												testId="action-button-submit"
											/>
										</Box>
										<Box xcss={styles.buttonWrapper}>
											<IconButton
												icon={(iconProps) => <CrossIcon {...iconProps} size="small" />}
												label={formatMessage(i18n.clearButtonLabel)}
												isDisabled={!fieldProps.value}
												onClick={() => handleClear(reset)}
												testId="action-button-clear"
											/>
										</Box>
									</Flex>
								)}
							</Box>
						)}
					</Field>
				</form>
			)}
		</Form>
	);
};
